import PropTypes from 'prop-types';
import Helmet from '../../components/Helmet';

import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Cancel, CheckCircle } from '@mui/icons-material';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { useGetPageContentQuery } from '../../services/seoContent';
import { useGetUserQuery } from '../../services/profile';
import { Icons, parseToken, windowExists } from '@classity-edtech/ui-base';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { useRelatedUsersQuery } from '../../services/users';
import { FormattedMessage } from 'react-intl';

export interface EmailConfirmationProps extends MuiBoxProps {}

export const EmailConfirmation = ({ className }: EmailConfirmationProps) => {
  const screens = useAppMediaQuery();
  const token =
    windowExists &&
    parseToken(
      (windowExists?.localStorage as { getItem: (key: string) => void })?.getItem('token'),
    );

  const { data: user } = useGetUserQuery({ userId: token.user }, { skip: !token.user });
  const { data: relatedUsers } = useRelatedUsersQuery({
    userId: token.user,
  });

  const { data, isLoading: fetchingContent } = useGetPageContentQuery({
    tag: 'emailConfirmation',
    contentType: 'page',
  });

  const getContent = () => {
    if (!data) return {};

    const sectionFields = data?.sections[0]?.fields;

    return {
      title: sectionFields?.title,
      text: sectionFields?.description?.content?.[0]?.content?.[0]?.value,
    };
  };

  const localisedMessage = (id: string) => {
    const F: any = FormattedMessage;
    return <F id={id} />;
  };

  return (
    <>
      <Helmet />
      {fetchingContent ? (
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          className={className}
          sx={{
            padding: { xs: 3, md: 5 },
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Stack
            sx={{
              gap: 2,
              justifyContent: 'center',
              alignItems: 'center',
              maxWidth: '500px',
              height: '100%',
              mx: 'auto',
            }}
          >
            <Icons.GridIcon
              sx={{
                mb: 4,
                width: '96px',
                height: '96px',
                color: globalColours['$global-colour-palette-gray-200'],
              }}
            />
            <Typography
              variant={screens.isDesktop ? 'h3' : 'h4'}
              data-testid='confirm-email-header'
              sx={{ textAlign: 'center' }}
            >
              {getContent().title}
            </Typography>
            <Typography variant='body3' sx={{ width: '100%', textAlign: 'center' }}>
              {getContent().text}
            </Typography>
            <Stack sx={{ gap: 2, width: '95%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <CheckCircle
                  fontSize='large'
                  sx={{
                    width: '24px',
                    height: '24px',
                    color: globalColours['$global-colour-sentiment-positive-active'],
                  }}
                />
                <Typography variant='body2'>
                  {localisedMessage('verify.email.personal.information')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <CheckCircle
                  fontSize='large'
                  sx={{
                    width: '24px',
                    height: '24px',
                    color: globalColours['$global-colour-sentiment-positive-active'],
                  }}
                />
                {relatedUsers?.length ? (
                  <Typography variant='body2'>
                    {relatedUsers?.length > 1
                      ? `${relatedUsers?.length} children`
                      : `${relatedUsers?.length} child`}{' '}
                    registered
                  </Typography>
                ) : (
                  <Typography variant='body2'>
                    {localisedMessage('verify.email.registration')}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Cancel
                  fontSize='large'
                  sx={{
                    width: '24px',
                    height: '24px',
                    color: globalColours['$global-colour-sentiment-warning-active'],
                  }}
                />
                <Typography variant='body2SemiBold'>
                  {localisedMessage('verify.email.email.verification')}{' '}
                  {user?.email && `- ${user?.email}`}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Box>
      )}
    </>
  );
};

EmailConfirmation.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

EmailConfirmation.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(EmailConfirmation)(
  ({ theme }) => `
`,
);
